import { PROGRAM_PROFILES } from "../data/programProfiles";
import { PERSONALITY_PROFILES } from "../data/personalityProfiles";
import { BEHAVIORAL_LANGUAGE } from "../data/behavioralLanguage";
import { PROGRAM_NAME_MAP }
from "../data/programMappings";

export function interpretBehavior(
  selectedProgram,
  personalityType,
  fitLevel,
  compatibilityData = null
)
 {
  if (!selectedProgram || !personalityType) {
    return null;
  }

  const mappedProgram =
    PROGRAM_NAME_MAP[selectedProgram.program] ||
    selectedProgram.program;

  const programProfile =
    PROGRAM_PROFILES[mappedProgram];

  const personalityProfile =
    PERSONALITY_PROFILES[personalityType];

  if (!programProfile || !personalityProfile) {
    return null;
  }

  const environmentTraits =
    programProfile.environmentTraits;

  // DETERMINE DOMINANT INSIGHT
  if (!environmentTraits) {
    return {
      alignments: [],
      tensions: [],
      adaptationPotential: [],
      fitTone: fitLevel
    };
  }

  const interpretation = {
    alignments: [],
    tensions: [],
    adaptationPotential: [],
    dominantInsight: null,
    fitTone: fitLevel
  };

  // COMPATIBILITY ENGINE INJECTION

  if (compatibilityData) {

    interpretation.alignments =
      compatibilityData.alignments || [];

    interpretation.tensions =
      compatibilityData.tensions || [];

    interpretation.narrativeSignals =
      compatibilityData.narrativeSignals || null;

    interpretation.dominantInsight =
      compatibilityData.dominantInsight || null;
    }
  
  // FIT-AWARE INTERPRETATION GOVERNANCE

  if (fitLevel === "High") {

    // High fit should prioritize alignment
    // over tension-heavy narratives

    if (
      interpretation.dominantInsight ===
      "adaptation_pressure"
    ) {
      interpretation.dominantInsight =
        "growth_alignment";
    }

    if (
      interpretation.dominantInsight ===
      "emotional_tension"
    ) {
      interpretation.dominantInsight =
        "people_alignment";
    }

    if (
      interpretation.dominantInsight ===
      "ambiguity_tension"
    ) {
      interpretation.dominantInsight =
        "creative_alignment";
    }
  }

  if (fitLevel === "Low") {

  // Low fit should NOT sound
  // overly optimistic

  if (
    interpretation.dominantInsight ===
    "structure_alignment"
  ) {
    interpretation.dominantInsight =
      "adaptation_pressure";
  }

  if (
    interpretation.dominantInsight ===
    "people_alignment"
  ) {
    interpretation.dominantInsight =
      "collaboration_tension";
  }

  if (
    interpretation.dominantInsight ===
    "creative_alignment"
  ) {
    interpretation.dominantInsight =
      "creative_tension";
  }
}

return interpretation;
}

export function composeBehaviorNarrative(
  interpretation,
  selectedProgram,
  personalityType
) {
  if (
    !interpretation ||
    !selectedProgram ||
    !personalityType
  ) {
    return "Program aligned with your assessment profile.";
  }

  const language =
    BEHAVIORAL_LANGUAGE;

  const SEMANTIC_ALIASES = {

  // ALIGNMENTS

  structured_environment:
    "structure_alignment",

  structured_reasoning:
    "structure_alignment",

  technical_environment:
    "technical_alignment",

  independent_execution:
    "technical_alignment",

  creative_environment:
    "creative_alignment",

  adaptive_environment:
    "adaptability_strength",

  leadership_alignment:
    "people_alignment",

  // TENSIONS

  high_collaboration:
    "collaboration_tension",

  high_ambiguity:
    "ambiguity_tension",

  emotional_intensity:
    "emotional_tension",

  low_structure_environment:
    "creativity_tension"
};

  const {
  tensions,
  adaptationPotential,
  fitTone,
  narrativeSignals
} = interpretation;

const semanticLanguage =
  language.environment_semantics;

const transitions =
  language.narrative_transitions;

const fitKey =
  fitTone.toLowerCase();

  let modifier = "";

  const dynamicNarratives = [];

  const dominantInsightKey =
    interpretation.dominantInsight &&
    typeof interpretation.dominantInsight === "object"
      ? interpretation.dominantInsight.alignment
      : interpretation.dominantInsight;

  const normalizedInsight =
    SEMANTIC_ALIASES[
      dominantInsightKey
    ] ||
    dominantInsightKey;

if (
  normalizedInsight &&
  language[normalizedInsight]?.[personalityType]
) {

  const narrative =
    language[normalizedInsight][personalityType];

  dynamicNarratives.push(

    typeof narrative === "string"
      ? narrative
      : narrative?.[fitTone.toLowerCase()]

  );

}

  let alignmentNarratives = [];

  let tensionNarratives = [];

  let adaptationNarratives = [];

  let layeredNarrative = [];

  const reinforcementTransition =
  transitions?.reinforcement?.[0] || "";

const cautionTransition =
  transitions?.caution?.[0] || "";

const adaptationTransition =
  transitions?.adaptation?.[0] || "";

  const primaryAlignment =
    narrativeSignals?.primaryAlignment;

  const primaryTension =
    narrativeSignals?.primaryTension;

  const secondaryAlignment =
    narrativeSignals?.secondaryAlignment;

  const secondaryTension =
    narrativeSignals?.secondaryTension;

  const narrativeWeighting =
    narrativeSignals?.narrativeWeighting;

  const adaptationPriority =
    narrativeSignals?.adaptationPriority;

  // NARRATIVE GOVERNANCE

  const strongAlignment =
    narrativeWeighting?.alignmentWeight ===
    "strong";

  const strongTension =
    narrativeWeighting?.tensionWeight ===
    "strong";

  const highAdaptationPressure =
    adaptationPriority === "high";

  const shouldSuppressMinorTension =
    fitTone === "High" &&
    strongAlignment &&
    !strongTension;

  const shouldEscalateAdaptation =
    fitTone !== "High" &&
    highAdaptationPressure;

  const shouldIncludeAdaptation =
    fitTone !== "High" ||
    highAdaptationPressure ||
    strongTension;

  if (
    interpretation.dominantInsight ===
    "structure_alignment"
  ) {
    modifier =
      "You seem to do your best when learning is well organised and expectations are clear.";
  }

  else if (
    interpretation.dominantInsight ===
    "creative_tension"
  ) {
    modifier =
      "You may find it takes a little longer to feel comfortable in courses where there are many open-ended tasks and no single right answer.";
  }

  else if (
    interpretation.dominantInsight ===
    "ambiguity_tension"
  ) {
    modifier =
       "Some parts of this programme may ask you to work without clear instructions, which could take some getting used to.";
  }

  else if (
    interpretation.dominantInsight ===
    "collaboration_tension"
  ) {
    modifier =
      "Working closely with other students on a regular basis may take a little more effort if you usually prefer working on your own.";
  }

  else if (
    interpretation.dominantInsight ===
    "emotional_tension"
  ) {
    modifier =
      "Some aspects of the programme may require patience and confidence when working closely with different people.";
  }

  else if (
    interpretation.dominantInsight ===
    "creative_alignment"
  ) {
    modifier =
      "You are likely to enjoy courses that encourage new ideas and different ways of thinking.";
  }

  else if (
    interpretation.dominantInsight ===
    "people_alignment"
  ) {
    modifier =
      "Working with other students, sharing ideas and taking part in discussions is likely to feel comfortable for you.";
  }

  else if (
    interpretation.dominantInsight ===
    "adaptation_pressure"
  ) {
    modifier =
    "This programme may encourage you to build skills that don't come naturally yet, giving you opportunities to grow as you progress through your studies.";
  }

  if (
  primaryAlignment &&
  semanticLanguage?.alignments?.[
    primaryAlignment
  ]
) {

  const alignmentNarrative =
    semanticLanguage.alignments?.[
      primaryAlignment
    ];

  if (alignmentNarrative) {

    alignmentNarratives.push(

      typeof alignmentNarrative === "string"

        ? alignmentNarrative

        : alignmentNarrative?.[fitKey] ||
          alignmentNarrative?.high ||
          alignmentNarrative?.moderate ||
          alignmentNarrative?.low

    );

  }
}

// Add a secondary behavioural explanation when it is
// different from the primary alignment.

if (
  secondaryAlignment &&
  secondaryAlignment !== primaryAlignment &&
  semanticLanguage?.alignments?.[
    secondaryAlignment
  ]
) {

  const secondaryNarrative =
    semanticLanguage.alignments[
      secondaryAlignment
    ];

  alignmentNarratives.push(

    typeof secondaryNarrative === "string"

      ? secondaryNarrative

      : secondaryNarrative?.[fitKey] ||
        secondaryNarrative?.high ||
        secondaryNarrative?.moderate ||
        secondaryNarrative?.low

  );

}

if (
  primaryTension &&
  semanticLanguage?.tensions?.[
    primaryTension
  ]
) {

  tensionNarratives.push(
    semanticLanguage.tensions[
      primaryTension
    ]
  );
}

if (
  secondaryTension &&
  semanticLanguage?.tensions?.[
    secondaryTension
  ]
) {

  const secondaryTensionNarrative =
    semanticLanguage.tensions[
      secondaryTension
    ];

  tensionNarratives.push(
    `Additional adjustment may also be required in areas related to ${secondaryTensionNarrative.toLowerCase()}.`
  );
}

// ADD TENSION CONTEXT

const suppressTensionNarrative =
  shouldSuppressMinorTension;

if (
  tensions.length > 0 &&
  interpretation.dominantInsight !==
    "collaboration_tension" &&
  !suppressTensionNarrative
) {

  const tensionNarrative =
    "Some aspects of the program may still require behavioral adjustment over time.";

  tensionNarratives.push(
    alignmentNarratives.length > 0 &&
    cautionTransition
      ? `${cautionTransition} ${tensionNarrative}`
      : tensionNarrative
  );
}

// ADD ADAPTATION CONTEXT

if (
  shouldIncludeAdaptation &&
  adaptationPotential.length > 0
) {

  const adaptationNarrative =
    shouldEscalateAdaptation
      ? "Successfully adapting to the program may require more deliberate behavioral adjustment and sustained flexibility over time."
      : "Your adaptability may help you navigate unfamiliar academic expectations more comfortably.";

  adaptationNarratives.push(
    tensionNarratives.length > 0 &&
    adaptationTransition
      ? `${adaptationTransition} ${adaptationNarrative}`
      : alignmentNarratives.length > 0 &&
        reinforcementTransition
      ? `${reinforcementTransition} ${adaptationNarrative}`
      : adaptationNarrative
  );
}

// FALLBACK TO STATIC MODIFIER
// Only use when neither semantic nor dynamic narratives exist.

if (
    alignmentNarratives.length === 0 &&
    tensionNarratives.length === 0 &&
    adaptationNarratives.length === 0 &&
    dynamicNarratives.length === 0 &&
    modifier
) {
    alignmentNarratives.push(modifier);
}

// FIT-AWARE OPENING

let openingNarrative = "";

if (primaryAlignment === "technical_environment") {

  openingNarrative =
    "Your natural approach to learning fits well with technically demanding environments where logical thinking and problem-solving are important.";

}

else if (primaryAlignment === "structured_environment") {

  openingNarrative =
    "You naturally perform well in organised learning environments where planning, consistency and accuracy are valued.";

}

else if (primaryAlignment === "creative_environment") {

  openingNarrative =
    "You appear well suited to environments that encourage creativity, curiosity and new ways of thinking.";

}

else if (primaryAlignment === "adaptive_environment") {

  openingNarrative =
    "You seem comfortable learning in environments that change, evolve and reward adaptability.";

}

else if (primaryAlignment === "leadership_alignment") {

  openingNarrative =
    "Your behavioural profile suggests strengths that fit environments requiring initiative, responsibility and decision-making.";

}

else if (fitTone === "High") {

  openingNarrative =
    "This programme matches the way you naturally like to learn and work.";

}

else if (fitTone === "Moderate") {

  openingNarrative =
    "Some parts of this programme are likely to suit you well, while others may take a little more time to get used to.";

}

else {

  openingNarrative =
    "This programme may not feel like the most natural fit at first, but that doesn't mean you can't succeed.";

}

// FIT-AWARE NARRATIVE ORDERING

if (fitTone === "High") {

  // Lead with strengths.
  layeredNarrative = [
    openingNarrative,

    ...tensionNarratives,

    ...alignmentNarratives,

    ...adaptationNarratives
  ];

  if (!shouldSuppressMinorTension) {
    layeredNarrative.push(...tensionNarratives);
  }

}

else if (fitTone === "Moderate") {

  // Present strengths first, then explain the main adjustment.
  layeredNarrative = [
    openingNarrative,
    ...alignmentNarratives,
    ...tensionNarratives,
    ...adaptationNarratives
  ];

}

else {

  // Low fit should immediately explain the biggest challenge.
  layeredNarrative = [
    openingNarrative,
    ...tensionNarratives,
    ...adaptationNarratives,
    ...alignmentNarratives
  ];
}

if (dynamicNarratives.length > 0) {
  layeredNarrative.splice(
    1,
    0,
    ...dynamicNarratives
  );
}

return layeredNarrative.join(" ");
}
